import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(date: Date | string, locale = 'fr-FR'): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function formatDateTime(date: Date | string, locale = 'fr-FR'): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str
  return str.slice(0, length) + '...'
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36)
}

// Risk severity colors
export const riskSeverityColors = {
  critical: 'text-risk-critical bg-risk-critical/10',
  high: 'text-risk-high bg-risk-high/10',
  medium: 'text-risk-medium bg-risk-medium/10',
  low: 'text-risk-low bg-risk-low/10',
  negligible: 'text-risk-negligible bg-risk-negligible/10',
}

// Maturity level colors
export const maturityLevelColors = {
  initial: 'text-maturity-initial bg-maturity-initial/10',
  developing: 'text-maturity-developing bg-maturity-developing/10',
  defined: 'text-maturity-defined bg-maturity-defined/10',
  managed: 'text-maturity-managed bg-maturity-managed/10',
  optimizing: 'text-maturity-optimizing bg-maturity-optimizing/10',
}

/**
 * Export an array of objects as CSV and trigger download
 */
export function exportToCSV<T extends Record<string, unknown>>(
  data: T[],
  filename: string,
  columns?: { key: keyof T; label: string }[]
): void {
  if (!data.length) return
  const cols = columns ?? (data?.length ? Object.keys(data[0]) : []).map((k) => ({ key: k as keyof T, label: String(k) }))
  const header = cols.map((c) => `"${String(c.label)}"`).join(',')
  const rows = (data ?? []).map((row) =>
    (cols ?? []).map((c) => {
      const val = row[c.key]
      if (val == null) return '""'
      const str = String(val).replace(/"/g, '""')
      return `"${str}"`
    }).join(',')
  )
  const csv = [header, ...rows].join('\n')
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Export data as JSON and trigger download
 */
export function exportToJSON(data: unknown, filename: string): void {
  const json = JSON.stringify(data, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.json`
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Export a list of events as a real .ics (iCalendar) file and trigger download.
 */
export function exportToICS(
  events: { uid?: string; title: string; start: string; end?: string; description?: string; location?: string }[],
  filename: string
): void {
  if (!events.length) return
  const toICSDate = (d: string) => {
    const dt = new Date(d)
    if (isNaN(dt.getTime())) return ''
    return dt.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
  }
  const esc = (s: string) => String(s ?? '').replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n')
  const now = toICSDate(new Date().toISOString())
  const lines: string[] = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//GovStratRiskMgn//AuditPRO//FR', 'CALSCALE:GREGORIAN']
  events.forEach((ev, i) => {
    const start = toICSDate(ev.start)
    if (!start) return
    const end = ev.end ? toICSDate(ev.end) : start
    lines.push('BEGIN:VEVENT')
    lines.push(`UID:${ev.uid ?? `${i}-${Date.now()}`}@govstratriskmgn`)
    lines.push(`DTSTAMP:${now}`)
    lines.push(`DTSTART:${start}`)
    lines.push(`DTEND:${end || start}`)
    lines.push(`SUMMARY:${esc(ev.title)}`)
    if (ev.description) lines.push(`DESCRIPTION:${esc(ev.description)}`)
    if (ev.location) lines.push(`LOCATION:${esc(ev.location)}`)
    lines.push('END:VEVENT')
  })
  lines.push('END:VCALENDAR')
  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${filename}-${new Date().toISOString().slice(0, 10)}.ics`
  a.click()
  URL.revokeObjectURL(url)
}
